import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Clock, BadgeCheck } from "lucide-react";
import { SITE } from "@/data/site";
import { useLang } from "@/lib/i18n";
import StarRating from "@/components/site/StarRating";
import GHLForm from "@/components/site/GHLForm";

export default function Hero() {
  const { t } = useLang();
  const videoRef = useRef(null);

  // Some browsers will refuse silent autoplay if the element is added late or
  // if the page hasn't been interacted with. Explicitly nudging .play() once
  // the element is mounted handles those edge cases without breaking the
  // happy-path autoplay attribute.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    // If the user clicks anywhere on the page, retry once.
    const onFirstInteract = () => {
      tryPlay();
      window.removeEventListener("pointerdown", onFirstInteract);
    };
    window.addEventListener("pointerdown", onFirstInteract, { once: true });
    return () => window.removeEventListener("pointerdown", onFirstInteract);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-brand-navy-dark text-white"
    >
      {/* Background video — looping, muted, autoplays. Blurred so text stays readable. */}
      <div className="absolute inset-0 pointer-events-none">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover scale-110 will-change-transform"
          style={{ filter: "blur(4px) brightness(0.55) saturate(1.1)" }}
          poster={SITE.trailerImage}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          data-testid="hero-video-bg"
        >
          {/* WebM (VP9) first — open codec, works in every modern browser
              including open-source Chromium. MP4 (H.264) as fallback for older
              builds and Safari (which prefers H.264 anyway). */}
          <source src="/hero-bg.webm" type="video/webm" />
          <source src="/hero-bg.mp4" type="video/mp4" />
        </video>
        {/* Fallback still — shown if the user's browser cannot play H.264 mp4. */}
        <img
          src={SITE.trailerImage}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover -z-10"
          style={{ filter: "blur(4px) brightness(0.55)" }}
        />
        {/* Layered colour wash so the navy brand stays dominant and copy stays sharp. */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark/80 via-brand-navy-dark/70 to-slate-900/85" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,191,0,0.10)_0%,transparent_55%)]" />
      </div>

      <div className="absolute -top-40 -right-32 w-[520px] h-[520px] rounded-full bg-brand-yellow/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-24 w-[460px] h-[460px] rounded-full bg-brand-navy-light/20 blur-3xl pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />

      <div className="relative container-max grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center py-12 sm:py-16 lg:py-20">
        <div className="fade-in-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-slate-900 px-3.5 py-1.5 text-xs font-bold tracking-wide shadow-lg shadow-yellow-300/20">
            <BadgeCheck size={14} />
            {t.hero.badge} · #{SITE.license}
          </div>

          {/* Headline — clean, no overlapping highlight, drop-shadow for legibility over video */}
          <h1
            className="mt-5 font-heading text-4xl sm:text-5xl lg:text-[3.55rem] xl:text-6xl font-bold tracking-tight text-white text-balance leading-[1.05]"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.45)" }}
          >
            {t.hero.headlinePre}{" "}
            <span className="text-brand-yellow">{t.hero.headlineHL}</span>
            {t.hero.headlinePost}
          </h1>

          <p
            className="mt-5 text-lg text-white leading-relaxed max-w-xl"
            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}
          >
            {t.hero.sub}
          </p>

          <div className="mt-6">
            <a
              href={SITE.googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-reviews"
              className="inline-block"
              aria-label="See our Google reviews"
            >
              <StarRating rating={SITE.rating} count={SITE.reviewCount} dark />
            </a>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="hero-call-cta"
              href={`tel:${SITE.primaryPhone}`}
              className="inline-flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-4 px-6 rounded-xl transition-colors shadow-lg shadow-yellow-300/30 whitespace-nowrap"
            >
              <Phone size={18} />
              <span>{t.cta.call} {SITE.primaryPhone}</span>
            </a>
            <Link
              data-testid="hero-quote-cta"
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/15 backdrop-blur-md border-2 border-white/40 hover:bg-white hover:text-slate-900 text-white font-bold py-4 px-6 rounded-xl transition-colors whitespace-nowrap"
            >
              {t.cta.freeEstimate}
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            <Stat value="12" label={t.hero.yearsServingFL} />
            <Stat value="24/7" label={t.hero.emergencyCalls} />
            <Stat value="4.9★" label={t.hero.googleRatingShort} />
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/90">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck size={15} className="text-brand-yellow" />
              {t.common.licensedInsured}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={15} className="text-brand-yellow" />
              {t.hero.dispatch247}
            </span>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-10 bg-brand-yellow text-slate-900 rounded-full px-4 py-1.5 text-xs font-bold shadow-md uppercase tracking-wider rotate-3">
            {t.common.freeEstimateTag}
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl shadow-black/40 ring-1 ring-white/10">
            <GHLForm minHeight={680} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div className="text-left">
      <p
        className="font-heading text-3xl font-bold text-brand-yellow leading-none"
        style={{ textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}
      >
        {value}
      </p>
      <p className="text-xs text-white/85 mt-1.5 font-medium">{label}</p>
    </div>
  );
}
